import type { ReactElement } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LandingPage } from './pages/LandingPage';
import { AuthWelcomePage } from './pages/AuthWelcomePage';
import { SignInPage } from './pages/SignInPage';
import { SignUpPage } from './pages/SignUpPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { MainLayout } from './layouts/MainLayout';
import { HomePage } from './pages/HomePage';
import { HistoryPage } from './pages/HistoryPage';
import { CreateBidPage } from './pages/CreateBidPage';
import { ReportsPage } from './pages/ReportsPage';
import { SubscriptionPage } from './pages/SubscriptionPage';
import { ProfilePage } from './pages/ProfilePage';
import { HelpSupportPage } from './pages/HelpSupportPage';
import { BidDetailPage } from './pages/BidDetailPage';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { ContactPage } from './pages/ContactPage';
import { AboutPage } from './pages/AboutPage';
import type { RootState } from './store';
import { fetchCurrentUser } from './services/authService';
import { setUser } from './store/slices/authSlice';

function RequireAuth({ children }: { children: ReactElement }) {
  const isAuthenticated = useSelector((s: RootState) => s.auth.isAuthenticated);
  const token = useSelector((s: RootState) => s.auth.token);
  if (!isAuthenticated || !token) {
    return <Navigate to="/signin" replace />;
  }
  return children;
}

function GuestOnly({ children }: { children: ReactElement }) {
  const isAuthenticated = useSelector((s: RootState) => s.auth.isAuthenticated);
  const token = useSelector((s: RootState) => s.auth.token);
  if (isAuthenticated && token) {
    return <Navigate to="/home" replace />;
  }
  return children;
}

/**
 * Scroll to top on route changes without a hash. When the URL has a fragment (e.g. /#features from
 * the footer while on /privacy), scroll that element into view after the next route paints.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = decodeURIComponent(hash.slice(1));
      if (!id) {
        window.scrollTo(0, 0);
        return;
      }
      const scrollToTarget = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollTo(0, 0);
        }
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(scrollToTarget);
      });
      return;
    }
    window.scrollTo(0, 0);
    const root = document.scrollingElement ?? document.documentElement;
    root.scrollTop = 0;
  }, [pathname, hash]);
  return null;
}

function UserRefresh() {
  const dispatch = useDispatch();
  const token = useSelector((s: RootState) => s.auth.token);
  const isAuthenticated = useSelector((s: RootState) => s.auth.isAuthenticated);

  useEffect(() => {
    if (!token || !isAuthenticated) return;
    let cancelled = false;
    fetchCurrentUser().then((u) => {
      if (u && !cancelled) dispatch(setUser(u));
    });
    return () => {
      cancelled = true;
    };
  }, [token, isAuthenticated, dispatch]);

  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <UserRefresh />
      <Routes>
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/about" element={<AboutPage />} />

        {/* No GuestOnly: signed-in users must still reach / and /#sections from footer/legal pages. */}
        <Route path="/" element={<LandingPage />} />
        <Route
          path="/signin/login"
          element={
            <GuestOnly>
              <SignInPage />
            </GuestOnly>
          }
        />
        <Route
          path="/signin"
          element={
            <GuestOnly>
              <AuthWelcomePage />
            </GuestOnly>
          }
        />
        <Route
          path="/signup"
          element={
            <GuestOnly>
              <SignUpPage />
            </GuestOnly>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <GuestOnly>
              <ForgotPasswordPage />
            </GuestOnly>
          }
        />
        <Route
          path="/reset-password"
          element={
            <GuestOnly>
              <ResetPasswordPage />
            </GuestOnly>
          }
        />

        <Route
          element={
            <RequireAuth>
              <MainLayout />
            </RequireAuth>
          }
        >
          <Route path="/home" element={<HomePage />} />
          <Route path="/history" element={<HistoryPage />} />
          <Route path="/create" element={<CreateBidPage />} />
          <Route path="/create/:editBidId" element={<CreateBidPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/help-support" element={<HelpSupportPage />} />
        </Route>

        <Route
          path="/bid/:bidId"
          element={
            <RequireAuth>
              <div className="min-h-screen bg-slate-50">
                <div className="max-w-3xl mx-auto px-4 py-4">
                  <BidDetailPage />
                </div>
              </div>
            </RequireAuth>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
